/**
 * 素材库详情组件
 *
 * 支持文档内容的查看、编辑和自动保存：
 * - 文档内容查看和编辑（基于 textarea 的 Markdown 编辑器）
 * - 元数据编辑（文件名、素材类型）
 * - 文档状态显示和向量化进度
 * - 1秒防抖自动保存机制
 * - 保存状态反馈（idle/saved/error）
 * - 向量化状态显示和手动触发
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { documentApi } from '../../services/documentApi';
import { knowledgeWebSocketService } from '../../services/knowledgeWebSocket';
import type { Document, MaterialType } from '../../types/document';
import { DocumentStatus } from '../../types/document';
import type { SyncProgressData, SyncCompletedData, SyncFailedData } from '../../services/knowledgeWebSocket';

// ==================== 常量定义 ====================

const FONT_SIZES = [12, 14, 16, 18, 20, 22, 24];
const LINE_HEIGHTS = [1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 2.5];
const DEBOUNCE_DELAY = 1000; // 防抖延迟1秒

const MATERIAL_TYPE_OPTIONS: { value: MaterialType; label: string }[] = [
  { value: 'novel' as MaterialType, label: '小说' },
  { value: 'technique' as MaterialType, label: '技巧' },
  { value: 'other' as MaterialType, label: '其他' },
];

// ==================== Props定义 ====================

export interface MaterialDetailProps {
  workspaceId: number;
  documentId: string;
  className?: string;
  onDocumentUpdate?: (document: Document) => void;
  onClose?: () => void;
}

// ==================== SVG图标常量 ====================

const LoadingIcon: React.FC = React.memo(() => (
  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500" />
));

const SaveIcon: React.FC = React.memo(() => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
));

const CheckIcon: React.FC = React.memo(() => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
));

const CloseIcon: React.FC = React.memo(() => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
));

// ==================== 主组件 ====================

/**
 * 素材库详情组件
 *
 * 功能：
 * - 显示和编辑文档内容
 * - 编辑文件名和素材类型
 * - 1秒防抖自动保存
 * - 向量化状态显示和手动触发
 * - WebSocket 实时进度更新
 */
export const MaterialDetail: React.FC<MaterialDetailProps> = ({
  workspaceId,
  documentId,
  className = '',
  onDocumentUpdate,
  onClose,
}) => {
  // ==================== 状态管理 ====================

  const [content, setContent] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const [materialType, setMaterialType] = useState<MaterialType>('other' as MaterialType);
  const [fontSize, setFontSize] = useState<number>(16);
  const [lineHeight, setLineHeight] = useState<number>(1.6);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 向量化相关状态
  const [vectorizationStatus, setVectorizationStatus] = useState<'indexed' | 'indexing' | 'not_indexed'>('not_indexed');
  const [isVectorizing, setIsVectorizing] = useState<boolean>(false);
  const [vectorizeError, setVectorizeError] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<SyncProgressData | null>(null);

  // 原始文档状态（用于对比变更）
  const [originalContent, setOriginalContent] = useState<string>('');
  const [documentStatus, setDocumentStatus] = useState<DocumentStatus>(DocumentStatus.UPLOADED);

  // Refs
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // ==================== 加载文档详情 ====================

  const loadDocumentDetail = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await documentApi.getDocumentDetail(undefined, documentId);

      setContent(response.content || '');
      setFilename(response.filename || '');
      setMaterialType((response.material_type || 'other') as MaterialType);
      setOriginalContent(response.content || '');
      setDocumentStatus(response.status || DocumentStatus.UPLOADED);

      // 更新向量化状态
      if (response.status === DocumentStatus.COMPLETED) {
        setVectorizationStatus('indexed');
      } else if (response.status === DocumentStatus.INDEXING) {
        setVectorizationStatus('indexing');
      } else {
        setVectorizationStatus('not_indexed');
      }

      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '加载失败';
      setError(errorMessage);
      setLoading(false);
    }
  }, [documentId]);

  // ==================== 防抖自动保存 ====================

  const autoSave = useCallback(async (updates: { content?: string; filename?: string; material_type?: MaterialType }) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        setSaveStatus('idle');

        const response = await documentApi.updateDocument(undefined, documentId, updates);

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);

        // 更新本地状态
        if (updates.content !== undefined) {
          setOriginalContent(updates.content);
        }
        if (updates.filename !== undefined) {
          setFilename(updates.filename);
        }
        if (updates.material_type !== undefined) {
          setMaterialType(updates.material_type);
        }

        // 通知父组件更新文档
        onDocumentUpdate?.(response as Document);

        // 如果内容变更，更新向量化状态
        if (updates.content !== undefined && updates.content !== originalContent) {
          setVectorizationStatus('not_indexed');
        }
      } catch (err) {
        console.error('[MaterialDetail] 保存失败:', err);
        setSaveStatus('error');
        setError(err instanceof Error ? err.message : '保存失败');
      } finally {
        setIsSaving(false);
      }
    }, DEBOUNCE_DELAY);
  }, [documentId, onDocumentUpdate, originalContent]);

  // ==================== 内容变更处理 ====================

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
    autoSave({ content: newContent });
  }, [autoSave]);

  const handleFilenameChange = useCallback((newFilename: string) => {
    setFilename(newFilename);
    autoSave({ filename: newFilename });
  }, [autoSave]);

  const handleMaterialTypeChange = useCallback((newType: MaterialType) => {
    setMaterialType(newType);
    autoSave({ material_type: newType });
  }, [autoSave]);

  // ==================== 手动保存 ====================

  const handleManualSave = useCallback(async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      setSaveStatus('idle');

      // 清除待处理的自动保存
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      const updates: { content?: string; filename?: string; material_type?: MaterialType } = {};

      // 检查哪些字段发生了变更
      if (content !== originalContent) {
        updates.content = content;
      }
      // 文件名和素材类型始终保存（因为它们可能是刚修改的）
      updates.filename = filename;
      updates.material_type = materialType;

      const response = await documentApi.updateDocument(undefined, documentId, updates);

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);

      // 更新原始内容
      setOriginalContent(content);

      // 通知父组件更新文档
      onDocumentUpdate?.(response as Document);

      // 如果内容变更，更新向量化状态
      if (updates.content !== undefined) {
        setVectorizationStatus('not_indexed');
      }
    } catch (err) {
      console.error('[MaterialDetail] 保存失败:', err);
      setSaveStatus('error');
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, content, originalContent, filename, materialType, documentId, onDocumentUpdate]);

  // ==================== 手动触发向量化 ====================

  const handleManualVectorize = useCallback(async () => {
    setIsVectorizing(true);
    setVectorizeError(null);
    setSyncProgress(null);

    try {
      // 1. 启动同步任务
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/knowledge/workspaces/${workspaceId}/sync`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            sync_materials: true,
            sync_settings: false,
            sync_chapters: false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: '启动同步失败' }));
        throw new Error(errorData.detail || '启动同步失败');
      }

      const startData = await response.json();
      const syncId = startData.sync_id;

      console.log('[MaterialDetail] 同步已启动:', { syncId, workspaceId });

      // 2. 通过 WebSocket 订阅同步进度
      knowledgeWebSocketService.subscribeSyncProgress(workspaceId, syncId, {
        onStarted: (data) => {
          console.log('[MaterialDetail] 同步开始:', data);
          setSyncProgress({
            sync_id: data.sync_id,
            stage: 'chroma',
            progress: 0,
            current: '同步开始...',
          });
        },
        onProgress: (data) => {
          console.log('[MaterialDetail] 同步进度:', data);
          setSyncProgress(data);
        },
        onCompleted: async (data: SyncCompletedData) => {
          console.log('[MaterialDetail] 同步完成:', data);
          setIsVectorizing(false);
          setSyncProgress(null);
          // 刷新数据以更新状态
          await loadDocumentDetail();
        },
        onFailed: (data: SyncFailedData) => {
          console.error('[MaterialDetail] 同步失败:', data);
          setIsVectorizing(false);
          setVectorizeError(data.error || '同步失败');
          setSyncProgress(null);
        },
        onError: (error) => {
          console.error('[MaterialDetail] WebSocket 错误:', error);
        },
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '触发向量化失败';
      setVectorizeError(errorMessage);
      setIsVectorizing(false);
      setSyncProgress(null);
    }
  }, [workspaceId, loadDocumentDetail]);

  // ==================== 重试向量化 ====================

  const handleRetryVectorize = useCallback(() => {
    setVectorizeError(null);
    handleManualVectorize();
  }, [handleManualVectorize]);

  // ==================== 效果钩子 ====================

  // 组件挂载时加载数据
  useEffect(() => {
    loadDocumentDetail();
  }, [loadDocumentDetail]);

  // 清理定时器和 WebSocket 订阅
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      knowledgeWebSocketService.close();
    };
  }, []);

  // ==================== 渲染 ====================

  /**
   * 获取向量化状态显示文本
   */
  const getStatusText = useCallback((): string => {
    if (syncProgress) {
      return `${syncProgress.current} (${syncProgress.progress}%)`;
    }
    switch (vectorizationStatus) {
      case 'indexed':
        return '已索引';
      case 'indexing':
        return '索引中...';
      case 'not_indexed':
        return '未索引';
      default:
        return '未知';
    }
  }, [vectorizationStatus, syncProgress]);

  /**
   * 获取向量化状态颜色类
   */
  const getStatusColorClass = useCallback((): string => {
    if (syncProgress) {
      return 'text-blue-600 bg-blue-50 border-blue-200';
    }
    switch (vectorizationStatus) {
      case 'indexed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'indexing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'not_indexed':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  }, [vectorizationStatus, syncProgress]);

  /**
   * 渲染加载状态
   */
  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center h-full bg-white ${className}`}>
        <LoadingIcon />
        <p className="mt-3 text-sm text-gray-700">加载中...</p>
      </div>
    );
  }

  /**
   * 渲染错误状态
   */
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center h-full bg-white ${className}`}>
        <p className="text-red-600 mt-2">{error}</p>
        <button
          type="button"
          className="mt-4 px-4 py-2 bg-spine-500 text-white rounded-md text-sm hover:bg-spine-600 transition-colors"
          onClick={loadDocumentDetail}
        >
          重试
        </button>
      </div>
    );
  }

  /**
   * 渲染进度条（当有同步进度时）
   */
  const renderProgressBar = () => {
    if (!syncProgress) return null;

    return (
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>{syncProgress.current}</span>
          <span>{syncProgress.progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-indigo-500 h-2 rounded-full transition-all duration-150"
            style={{ width: `${syncProgress.progress}%` }}
          />
        </div>
      </div>
    );
  };

  /**
   * 渲染内容
   */
  return (
    <div className={`h-full flex flex-col bg-white ${className}`}>
      {/* 头部信息 */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between w-full">
          <div className="flex-1">
            {/* 文件名编辑 */}
            <input
              type="text"
              value={filename}
              onChange={(e) => handleFilenameChange(e.target.value)}
              className="text-lg font-medium text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-spine-500 focus:outline-none px-1 py-0.5"
              placeholder="文件名"
            />

            <div className="mt-2 flex items-center gap-3 text-xs text-gray-700">
              {/* 素材类型选择 */}
              <select
                value={materialType}
                onChange={(e) => handleMaterialTypeChange(e.target.value as MaterialType)}
                className="px-2 py-1 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-500"
              >
                {MATERIAL_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {/* 文档状态 */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                documentStatus === DocumentStatus.COMPLETED ? 'bg-green-100 text-green-700' :
                documentStatus === DocumentStatus.FAILED ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {documentStatus === DocumentStatus.COMPLETED ? '已完成' :
                 documentStatus === DocumentStatus.FAILED ? '失败' :
                 documentStatus === DocumentStatus.UNINDEXED ? '需重新索引' :
                 documentStatus}
              </span>
            </div>

            {/* 进度条 */}
            {renderProgressBar()}
          </div>

          {/* 向量化状态和操作按钮 */}
          <div className="flex items-center gap-2">
            {/* 向量化状态 */}
            <span className={`px-3 py-1 text-xs rounded-full border ${getStatusColorClass()}`}>
              {getStatusText()}
            </span>

            {/* 手动触发向量化按钮 */}
            {vectorizationStatus !== 'indexed' && !isVectorizing && (
              <button
                type="button"
                onClick={handleManualVectorize}
                className="px-3 py-1.5 text-xs bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors disabled:opacity-50"
                disabled={isSaving}
              >
                立即索引
              </button>
            )}

            {/* 向量化中显示 */}
            {isVectorizing && !syncProgress && (
              <div className="flex items-center gap-1 text-xs text-yellow-600">
                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                索引中...
              </div>
            )}

            {/* 关闭按钮 */}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="关闭"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>

        {/* 向量化错误提示 */}
        {vectorizeError && (
          <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
            <span className="text-xs text-red-700">{vectorizeError}</span>
            <button
              type="button"
              onClick={handleRetryVectorize}
              className="text-xs text-red-600 hover:text-red-800 underline"
            >
              重试
            </button>
          </div>
        )}

        {/* 错误提示 */}
        {error && saveStatus === 'error' && (
          <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
            <span className="text-xs text-red-700">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="p-1 hover:bg-red-100 rounded"
            >
              <CloseIcon />
            </button>
          </div>
        )}
      </div>

      {/* 工具栏 */}
      <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* 字体大小 */}
          <div className="flex items-center gap-1">
            <select
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-500"
            >
              {FONT_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}px
                </option>
              ))}
            </select>
          </div>

          {/* 行间距 */}
          <div className="flex items-center gap-1">
            <select
              value={lineHeight}
              onChange={(e) => setLineHeight(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-500"
            >
              {LINE_HEIGHTS.map((height) => (
                <option key={height} value={height}>
                  {height.toFixed(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 保存按钮和状态 */}
        <div className="flex items-center gap-2">
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckIcon />
              已保存
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-red-600">保存失败</span>
          )}
          {isSaving && (
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <LoadingIcon />
              保存中...
            </span>
          )}
          <button
            type="button"
            onClick={handleManualSave}
            disabled={isSaving}
            className="px-3 py-1.5 text-sm bg-ink-800 hover:bg-ink-700 text-white rounded shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 flex items-center gap-1"
          >
            <SaveIcon />
            保存
          </button>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <textarea
          value={content}
          onChange={(e) => handleContentChange(e.target.value)}
          className="w-full h-full resize-none focus:outline-none text-gray-900 font-mono leading-relaxed"
          style={{
            fontSize: `${fontSize}px`,
            lineHeight: `${lineHeight}`,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
          placeholder="在此编辑文档内容..."
        />
      </div>
    </div>
  );
};

export default MaterialDetail;
